import type { FC, ReactNode } from 'react'
import type { ReportSection } from '@/types'

import { BarChart } from '@/components/charts/bar-chart'
import { LineChart } from '@/components/charts/line-chart'

interface ReportFigureProps {
    section: ReportSection
    reportCode: string
    figureNumber: number
}

export const ReportFigure: FC<ReportFigureProps> = ({ section, reportCode, figureNumber }): ReactNode => {
    const data = section.figureData

    if (!data) return null

    const label = `Fig. ${reportCode.replace(/^FR-/i, '')}.${figureNumber}`
    const title = section.figureTitle || 'Figure'

    return (
        <figure className='my-10 border border-hairline'>
            <div className='flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b border-hairline px-4 py-3'>
                <p className='hud text-ink'>
                    {label} /&nbsp;

                    <span data-mdlfy-cms={`report_sections:${section.id}:figure_title`}>
                        {section.figureTitle}
                    </span>
                </p>

                {data.unit && (
                    <p className='hud text-mute'>
                        Unit / {data.unit}
                    </p>
                )}
            </div>

            <div className='px-4 pb-4 pt-5'>
                {section.figureKind === 'bars' ? (
                    <BarChart
                        title={title}
                        labels={data.labels}
                        values={data.series[0]?.values ?? []}
                        unit={data.unit}
                    />
                ) : (
                    <LineChart
                        title={title}
                        labels={data.labels}
                        series={data.series}
                        unit={data.unit}
                        target={data.threshold !== undefined ? { value: data.threshold, label: data.thresholdLabel || 'Threshold' } : undefined}
                        height={240}
                    />
                )}
            </div>

            {section.figureCaption && (
                <figcaption className='border-t border-hairline px-4 py-3 text-[13px] leading-[1.55] text-mute'>
                    <span data-mdlfy-cms={`report_sections:${section.id}:figure_caption`}>
                        {section.figureCaption}
                    </span>
                </figcaption>
            )}
        </figure>
    )
}
